/* ============================================================
   Craft a Brew — ABV Calculator
   Self-contained; loads Lucide icons dynamically if not
   already present on the page.
   ============================================================ */

(function () {
  'use strict';

  // Load Lucide icon library if not already on the page
  function ensureLucide(cb) {
    if (window.lucide) { cb(); return; }
    var s = document.createElement('script');
    s.src = 'https://unpkg.com/lucide@latest/dist/umd/lucide.min.js';
    s.onload = function () { cb(); };
    s.onerror = function () { cb(); }; // proceed even if icon load fails
    document.head.appendChild(s);
  }

  // ==========================================================
  // STATE
  // ==========================================================
  var state = {
    tool: 'hydrometer',
    unit: 'sg',
    formula: 'standard',
    wcf: 1.04,
    tempCorrect: false,
    sampleTemp: 68,
    calTemp: 68
  };

  // ==========================================================
  // UNIT CONVERSIONS
  // ==========================================================
  function brixToSG(brix) {
    return 1 + (brix / (258.6 - ((brix / 258.2) * 227.1)));
  }
  function sgToBrix(sg) {
    return (((182.4601 * sg - 775.6821) * sg + 1262.7794) * sg - 669.5622);
  }
  function platoToSG(plato) { return brixToSG(plato); }
  function sgToPlato(sg) {
    return -616.868 + (1111.14 * sg) - (630.272 * sg * sg) + (135.997 * sg * sg * sg);
  }
  function toSG(value, unit) {
    if (unit === 'sg') return value;
    if (unit === 'brix') return brixToSG(value);
    if (unit === 'plato') return platoToSG(value);
    return value;
  }
  function fromSG(sg, unit) {
    if (unit === 'sg') return sg;
    if (unit === 'brix') return sgToBrix(sg);
    if (unit === 'plato') return sgToPlato(sg);
    return sg;
  }

  // ==========================================================
  // ABV FORMULAS
  // ==========================================================
  function abvStandard(og, fg) { return (og - fg) * 131.25; }
  function abvAccurate(og, fg) {
    // M. Hall / BeerSmith accurate formula
    if (og <= 1.000 || (1.775 - og) === 0) return 0;
    return (76.08 * (og - fg) / (1.775 - og)) * (fg / 0.794);
  }

  // ==========================================================
  // REFRACTOMETER CORRECTION (Novotny cubic)
  // Used by Brewer's Friend, BeerSmith, and most modern
  // brewing calculators. Gives physically plausible FG values
  // across the full range of realistic refractometer readings.
  // ==========================================================
  function refracCorrectFG(ogBrix, fgBrix, wcf) {
    var ob = ogBrix / wcf;
    var fb = fgBrix / wcf;
    return 1.001843
         - 0.002318474 * ob
         - 0.000007775 * ob * ob
         - 0.000000034 * ob * ob * ob
         + 0.00574 * fb
         + 0.00003344 * fb * fb
         + 0.000000086 * fb * fb * fb;
  }
  function refracCorrectOG(ogBrix, wcf) { return brixToSG(ogBrix / wcf); }

  // ==========================================================
  // TEMPERATURE CORRECTION (Lincoln Brewing, °F)
  // ==========================================================
  function tempFactor(tempF) {
    return 1.00130346
         - 1.34722124e-4 * tempF
         + 2.04052596e-6 * tempF * tempF
         - 2.32820948e-9 * tempF * tempF * tempF;
  }
  function correctSG(measuredSG, sampleF, calF) {
    return measuredSG * (tempFactor(sampleF) / tempFactor(calF));
  }

  // ==========================================================
  // CALORIES (per 12 oz)
  // Palmer/ASBC formula: Cal = 3.55 × FG × (6.9×ABW + 4×(RE-0.1))
  // ABW as percent, RE in Plato, FG as specific gravity.
  // 3.5488 converts 100 ml to 12 fl oz (354.88 ml).
  // ==========================================================
  function caloriesPer12oz(og, fg) {
    if (og <= 1.000 || fg <= 0 || fg > og) return 0;
    var ogP = sgToPlato(og);
    var fgP = sgToPlato(fg);
    var realExtract = (0.1808 * ogP) + (0.8192 * fgP);
    var abv = abvStandard(og, fg);
    var abw = abv * 0.79336;
    var cal100ml = ((6.9 * abw) + 4.0 * Math.max(0, realExtract - 0.1)) * fg;
    return cal100ml * 3.5488;
  }

  function apparentAttenuation(og, fg) {
    if (og <= 1.000) return 0;
    return ((og - fg) / (og - 1)) * 100;
  }

  // ==========================================================
  // ABV CATEGORY
  // ==========================================================
  function getCategory(abv) {
    var cats = [
      { max: 3.5,  desc: 'Session strength &mdash; easy-drinking and built for another round.' },
      { max: 6.0,  desc: 'Standard strength &mdash; the sweet spot for craft beer.' },
      { max: 8.0,  desc: 'Strong territory &mdash; warming, complex, sip-worthy.' },
      { max: 12.0, desc: 'Imperial strength &mdash; big, bold, best shared in small pours.' },
      { max: 20.0, desc: 'Extreme territory &mdash; barleywine, eisbock, or cider/wine range.' }
    ];
    var position;
    if (abv < 8.0) position = (abv / 8.0) * 75;
    else position = 75 + Math.min(25, ((abv - 8.0) / 8.0) * 25);
    position = Math.max(0, Math.min(100, position));
    for (var i = 0; i < cats.length; i++) {
      if (abv < cats[i].max) return { desc: cats[i].desc, position: position };
    }
    return { desc: cats[cats.length - 1].desc, position: 100 };
  }

  // ==========================================================
  // VALIDATION
  // ==========================================================
  function validate(og, fg) {
    var w = [];
    if (isNaN(og) || isNaN(fg)) return w;

    if (og < 1.000 || og > 1.200) {
      w.push({ type: 'error', icon: 'alert-circle',
        text: 'OG is outside the plausible range for beer (1.000&ndash;1.200). Double-check your reading or units.' });
    }
    if (fg < 0.985 || fg > 1.060) {
      w.push({ type: 'error', icon: 'alert-circle',
        text: 'FG is outside the plausible range for beer (0.985&ndash;1.060). Double-check your reading or units.' });
    }
    if (og > 1.000 && fg >= og) {
      w.push({ type: 'error', icon: 'alert-circle',
        text: 'FG is at or above OG &mdash; your beer hasn\'t fermented, or the readings may be swapped.' });
      return w;
    }
    if (og > fg && (og - fg) < 0.004 && og > 1.020) {
      w.push({ type: 'caution', icon: 'alert-triangle',
        text: 'Very small gravity drop. Fermentation may be stuck &mdash; check temperature and yeast viability before bottling.' });
    }
    var atten = apparentAttenuation(og, fg);
    if (og > 1.030 && atten > 0 && atten < 55) {
      w.push({ type: 'caution', icon: 'alert-triangle',
        text: 'Low attenuation (' + atten.toFixed(0) + '%). If fermentation looks done, your yeast may have underperformed.' });
    }
    if (atten > 95) {
      w.push({ type: 'caution', icon: 'alert-triangle',
        text: 'Unusually high attenuation (' + atten.toFixed(0) + '%). Double-check hydrometer calibration and that you\'re reading the bottom of the meniscus.' });
    }
    return w;
  }

  // ==========================================================
  // RENDER
  // ==========================================================
  function render() {
    var ogEl = document.getElementById('ogInput');
    var fgEl = document.getElementById('fgInput');
    var ogDisplay = parseFloat(ogEl.value);
    var fgDisplay = parseFloat(fgEl.value);
    if (isNaN(ogDisplay)) ogDisplay = 0;
    if (isNaN(fgDisplay)) fgDisplay = 0;

    var og, fg;

    if (state.tool === 'refractometer') {
      var ogBrix = state.unit === 'sg' ? sgToBrix(ogDisplay)
                 : state.unit === 'plato' ? ogDisplay
                 : ogDisplay;
      var fgBrix = state.unit === 'sg' ? sgToBrix(fgDisplay)
                 : state.unit === 'plato' ? fgDisplay
                 : fgDisplay;
      og = refracCorrectOG(ogBrix, state.wcf);
      fg = refracCorrectFG(ogBrix, fgBrix, state.wcf);
    } else {
      og = toSG(ogDisplay, state.unit);
      fg = toSG(fgDisplay, state.unit);
      if (state.tempCorrect) {
        og = correctSG(og, state.sampleTemp, state.calTemp);
        fg = correctSG(fg, state.sampleTemp, state.calTemp);
      }
    }

    if (!isFinite(og) || !isFinite(fg)) { og = 1; fg = 1; }

    var abv = state.formula === 'accurate' ? abvAccurate(og, fg) : abvStandard(og, fg);
    abv = Math.max(0, isFinite(abv) ? abv : 0);
    var atten = Math.max(0, apparentAttenuation(og, fg));
    var cal = Math.max(0, caloriesPer12oz(og, fg));

    document.getElementById('abvValue').textContent = abv.toFixed(1) + '%';
    document.getElementById('attenValue').textContent = atten.toFixed(0) + '%';
    document.getElementById('calValue').textContent = Math.round(cal);

    var cat = getCategory(abv);
    var fill = document.getElementById('categoryFill');
    var marker = document.getElementById('categoryMarker');
    var nameEl = document.getElementById('categoryName');
    if (abv > 0 && og > fg) {
      fill.style.width = cat.position + '%';
      marker.style.left = cat.position + '%';
      marker.style.display = 'block';
      nameEl.innerHTML = cat.desc;
    } else {
      fill.style.width = '0%';
      marker.style.left = '0%';
      nameEl.textContent = 'Enter your gravity readings above to see where your beer lands.';
    }

    var warns = validate(og, fg);
    var wBox = document.getElementById('warnings');
    if (warns.length) {
      wBox.innerHTML = warns.map(function (w) {
        return '<div class="warning ' + w.type + '">' +
               '<i data-lucide="' + w.icon + '" class="cab-icon"></i>' +
               '<span>' + w.text + '</span></div>';
      }).join('');
      if (window.lucide && window.lucide.createIcons) window.lucide.createIcons();
    } else {
      wBox.innerHTML = '';
    }
  }

  // ==========================================================
  // UNIT & TOOL SWITCHING
  // ==========================================================
  // ==========================================================
  // DEFAULTS & RESET
  // Hydrometer defaults are 1.055/1.010 SG; refractometer
  // defaults are 13.6/6.5 raw Brix (uncorrected readings that
  // produce ~6% ABV after the Novotny correction). Switching
  // tools resets to the appropriate defaults rather than blindly
  // converting — a 1.010 SG hydrometer reading converts to 2.6
  // Brix, which is physically impossible as a refractometer FG
  // reading and produces garbage through the correction formula.
  // ==========================================================
  function resetToDefaults() {
    var ogEl = document.getElementById('ogInput');
    var fgEl = document.getElementById('fgInput');
    if (state.tool === 'hydrometer') {
      var ogSG = 1.055;
      var fgSG = 1.010;
      ogEl.value = formatForUnit(fromSG(ogSG, state.unit), state.unit);
      fgEl.value = formatForUnit(fromSG(fgSG, state.unit), state.unit);
    } else {
      var ogBrix = 13.6;
      var fgBrix = 6.5;
      if (state.unit === 'sg') {
        ogEl.value = brixToSG(ogBrix).toFixed(3);
        fgEl.value = brixToSG(fgBrix).toFixed(3);
      } else {
        ogEl.value = ogBrix.toFixed(1);
        fgEl.value = fgBrix.toFixed(1);
      }
    }
    ogEl.step = state.unit === 'sg' ? '0.001' : '0.1';
    fgEl.step = state.unit === 'sg' ? '0.001' : '0.1';
  }

  function formatForUnit(value, unit) {
    if (unit === 'sg') return value.toFixed(3);
    return value.toFixed(1);
  }

  function switchUnit(newUnit) {
    if (newUnit === state.unit) return;
    var ogEl = document.getElementById('ogInput');
    var fgEl = document.getElementById('fgInput');
    var ogVal = parseFloat(ogEl.value) || 0;
    var fgVal = parseFloat(fgEl.value) || 0;
    var ogSG = toSG(ogVal, state.unit);
    var fgSG = toSG(fgVal, state.unit);
    state.unit = newUnit;
    ogEl.value = formatForUnit(fromSG(ogSG, newUnit), newUnit);
    fgEl.value = formatForUnit(fromSG(fgSG, newUnit), newUnit);
    ogEl.step = newUnit === 'sg' ? '0.001' : '0.1';
    fgEl.step = newUnit === 'sg' ? '0.001' : '0.1';
    render();
  }

  function switchTool(newTool) {
    if (newTool === state.tool) return;
    state.tool = newTool;
    var refracSection = document.getElementById('refracSection');
    var tipText = document.getElementById('tipText');
    if (newTool === 'refractometer') {
      refracSection.style.display = 'block';
      if (state.unit === 'sg') {
        state.unit = 'brix';
        document.querySelectorAll('#unitToggle .toggle-btn').forEach(function (b) {
          b.classList.toggle('active', b.dataset.unit === 'brix');
        });
      }
      tipText.innerHTML = 'Refractometers read FG too high once alcohol is present. We apply the correction automatically &mdash; just enter your readings as measured.';
    } else {
      refracSection.style.display = 'none';
      tipText.innerHTML = 'Take OG before pitching yeast. Take FG once your gravity is stable for 2&ndash;3 days.';
    }
    resetToDefaults();
    render();
  }

  // ==========================================================
  // BOOT
  // ==========================================================
  function bindToggleGroup(groupId, onSelect) {
    var group = document.getElementById(groupId);
    if (!group) return;
    group.querySelectorAll('.toggle-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        group.querySelectorAll('.toggle-btn').forEach(function (b) { b.classList.remove('active'); });
        btn.classList.add('active');
        onSelect(btn);
      });
    });
  }

  function init() {
    bindToggleGroup('toolToggle', function (btn) { switchTool(btn.dataset.tool); });
    bindToggleGroup('unitToggle', function (btn) { switchUnit(btn.dataset.unit); });
    bindToggleGroup('formulaToggle', function (btn) {
      state.formula = btn.dataset.formula;
      render();
    });

    document.getElementById('wcfInput').addEventListener('input', function () {
      var v = parseFloat(this.value);
      state.wcf = (isFinite(v) && v > 0.5 && v < 1.5) ? v : 1.04;
      render();
    });

    document.getElementById('tempCheck').addEventListener('change', function () {
      state.tempCorrect = this.checked;
      document.getElementById('tempInputs').style.display = this.checked ? 'grid' : 'none';
      render();
    });

    document.getElementById('sampleTempInput').addEventListener('input', function () {
      var v = parseFloat(this.value);
      state.sampleTemp = (isFinite(v) && v > 0 && v < 200) ? v : 68;
      render();
    });

    document.getElementById('calTempInput').addEventListener('change', function () {
      state.calTemp = parseFloat(this.value) || 68;
      render();
    });

    document.getElementById('ogInput').addEventListener('input', render);
    document.getElementById('fgInput').addEventListener('input', render);

    var advBtn = document.getElementById('advancedToggle');
    var advPanel = document.getElementById('advancedPanel');
    advBtn.addEventListener('click', function () {
      var isOpen = advBtn.classList.toggle('open');
      advPanel.classList.toggle('open', isOpen);
      advBtn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });

    document.querySelectorAll('.tip-close').forEach(function (btn) {
      btn.addEventListener('click', function () {
        this.parentElement.style.display = 'none';
      });
    });

    if (window.lucide && window.lucide.createIcons) window.lucide.createIcons();
    render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { ensureLucide(init); });
  } else {
    ensureLucide(init);
  }
})();